import { History } from 'history';
import React from 'react';
import styled from 'styled-components';

import { themes } from 'kb-shared';
import { JourneyType } from 'kb-shared/utilities/myJourney';
import { Appointment, PatientToDo } from 'kb-shared/types';

import { buildCompactJourney } from 'screens/MyJourney/journeySteps';
import { journeyIcon } from 'assets/icons';
import StepIcon from './steps';

type CompleteProps = {
  complete: boolean;
};

type JourneyStepProps = {
  highlight: boolean;
};

interface ResultValue {
  date: string;
}

interface Result {
  afc: ResultValue[];
  labs: ResultValue[];
}

type MyJourney = {
  journeyType: JourneyType;
  appointments: Appointment[];
  results: Result;
  patientTodos: PatientToDo[];
  cyclePlan: {
    id: string;
    finished?: string;
    expectedDeliveryDate?: string;
    deliveryDate?: string;
  };
};

type Props = {
  history: History;
  myJourney: MyJourney;
};

export const JourneyWidget = ({ history, myJourney }: Props) => {
  const { cyclePlan, patientTodos, results, appointments, journeyType } = myJourney;

  const journey = buildCompactJourney({
    appointments,
    patientTodos,
    results,
    triggerInstruction: null,
    journeyType,
    cyclePlan
  });

  if (!journey) {
    return null;
  }

  return (
    <Container>
      <JourneyIcon src={journeyIcon} alt={'icon'} />
      <Text>My Journey</Text>
      <SubTitle>Track your progress and keep abreast of what's to come in your journey.</SubTitle>
      <JourneyWrapper>
        {journey.steps.map((step, idx: number) => {
          const isCurrent = step.date === 'Current';

          return (
            <JourneyStep key={`${step.title}`} highlight={isCurrent}>
              <StepIcon
                isFirst={idx === 0}
                isLast={idx === journey.steps.length - 1}
                current={isCurrent}
                completed={step.isComplete}
              />
              <SubText complete={step.isComplete}>{step.title}</SubText>
            </JourneyStep>
          );
        })}
      </JourneyWrapper>
      <Button onClick={() => history.push('/my-journey')}>VIEW MORE</Button>
    </Container>
  );
};

const JourneyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items; center;
  width: 100%;
`;

const JourneyStep = styled.div<JourneyStepProps>`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  height: 40px;
  padding: 0px 20px;
  background-color: ${({ highlight }: JourneyStepProps) => (highlight ? 'white' : 'inherit')};
`;

const Button = styled.button`
  ${({ theme }) => theme.fonts.FoundersGrotesk};
  width: 126px;
  height: 48px;
  border-radius: 24px;
  background-color ${themes.colors.KBNight};
  font-size: 14px;
  text-transform: uppercase;
  outline: none;
  margin-top: 50px;
  color: white;
`;

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-direction: column;
  border: solid 4px ${themes.colors.KBNight};
  background-color: ${themes.colors.pink};
  padding: 20px 0px;
  min-height: 100%;
`;

const Text = styled.div`
  ${({ theme }) => theme.fonts.DomaineDisplay};
  font-size: 24px;
  text-align: center;
  color: ${themes.colors.KBNight};
  margin-top: 10px;
`;

const SubTitle = styled.div`
  ${({ theme }) => theme.fonts.FoundersGrotesk};
  font-size: 14px;
  color: ${themes.colors.KBNight};
  padding: 0px 25px 20px 25px;
  margin-top: 12px;
`;

const SubText = styled.div<CompleteProps>`
  ${({ theme }) => theme.fonts.FoundersGrotesk};
  font-size: 14px;
  color: ${({ complete }: CompleteProps) => (complete ? 'white' : themes.colors.KBNight)};
  padding: 0px 25px;
`;

const JourneyIcon = styled.img`
  width: 81px;
  height: 75px;
  object-fit: contain;
`;
